import type {ClientTool, ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ToolCallResult, ToolResultTextContent} from '@microsoft/agent-host-protocol'

export const VIEW_SESSIONS_TOOL_NAME = 'view_sessions'

export interface ViewSessionsClientToolDeps {
  /** Navigate the browser to the cloud agent session with the given task id. */
  navigateToSession: (taskId: string) => void
}

const VIEW_SESSIONS_TOOL_DEFINITION: ClientTool['definition'] = {
  name: VIEW_SESSIONS_TOOL_NAME,
  title: 'Open a session',
  description:
    "Open one of the user's Copilot cloud agent sessions by taking them to it. Use when the user wants to " +
    'go to, open, or view a specific background session (for example "open the session about auth" or "take ' +
    'me to that session"). Pass the session id from a list_sessions result. Do not invent ids.',
  inputSchema: {
    type: 'object',
    properties: {
      session_id: {
        type: 'string',
        description: 'The id of the session to open, as returned by list_sessions.',
      },
    },
    required: ['session_id'],
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
  },
}

/**
 * `ClientTool` implementation of `view_sessions`. Navigates the browser to a
 * specific cloud agent session (mirroring the desktop app's `navigate_to`); the
 * relay runtime owns routing and completion.
 */
export function buildViewSessionsClientTool(deps: ViewSessionsClientToolDeps): ClientTool {
  return {
    definition: VIEW_SESSIONS_TOOL_DEFINITION,
    fulfil: call => fulfilViewSessions(call, deps),
  }
}

function fulfilViewSessions(call: ClientToolCall, deps: ViewSessionsClientToolDeps): ToolCallResult {
  try {
    const taskId = parseViewSessionsInput(call.toolInput)
    deps.navigateToSession(taskId)
    return {
      success: true,
      pastTenseMessage: 'Opened the session',
      content: [textToolContent('Navigating the user to the session now.')],
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      pastTenseMessage: 'Failed to open the session',
      error: {message},
      content: [textToolContent(message)],
    }
  }
}

/**
 * Parse the `session_id` from the tool input, accepting either a bare id or a
 * cloud-agent task path/URL (`.../tasks/{id}`) and reducing it to the id.
 */
export function parseViewSessionsInput(toolInput: string | undefined): string {
  if (!toolInput) throw new Error(`${VIEW_SESSIONS_TOOL_NAME} requires a session_id.`)

  let parsed: unknown
  try {
    parsed = JSON.parse(toolInput)
  } catch {
    throw new Error(`${VIEW_SESSIONS_TOOL_NAME} requires JSON input.`)
  }

  const raw = parsed && typeof parsed === 'object' ? (parsed as {session_id?: unknown}).session_id : undefined
  if (typeof raw !== 'string' || !raw.trim()) {
    throw new Error(`${VIEW_SESSIONS_TOOL_NAME} requires a non-empty session_id.`)
  }

  let taskId: string
  try {
    taskId = taskIdFromInput(raw.trim())
  } catch {
    // `taskIdFromInput` decodes the id, which throws on malformed percent-encoding.
    throw new Error(`${VIEW_SESSIONS_TOOL_NAME} requires a valid session_id.`)
  }
  // Reject empties and the `.`/`..` path segments the router would resolve to a
  // different route than the requested session.
  if (!taskId.trim() || taskId === '.' || taskId === '..') {
    throw new Error(`${VIEW_SESSIONS_TOOL_NAME} requires a valid session_id.`)
  }
  return taskId
}

/** Reduce a `.../tasks/{id}` path or URL to its id; a bare id passes through. */
export function taskIdFromInput(value: string): string {
  const marker = '/tasks/'
  // Strip any query/fragment first so a marker inside `?...`/`#...` (e.g.
  // `.../tasks/abc?next=/tasks/def`) can't select the wrong id.
  const pathOnly = value.split(/[?#]/, 1)[0] ?? value
  const index = pathOnly.lastIndexOf(marker)
  if (index === -1) return value
  let id = pathOnly.slice(index + marker.length)
  const slash = id.indexOf('/')
  if (slash !== -1) id = id.slice(0, slash)
  return decodeURIComponent(id)
}

function textToolContent(text: string): ToolResultTextContent {
  return {type: 'text', text} as ToolResultTextContent
}
